import {
  COLORING_GRID_PRICE_LIST,
  ColoringGridStatusConstants,
  GOTIVA_ARTIST_DOC_ID,
  ONLY_APPROVED_ARTISTS_QUERY,
} from 'app-constants';
import { ALL_COLORING_GRID_CATEGORIES_QUERY } from 'app-constants/coloring-grid-categories';
import { collection, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { useForm } from 'hooks';
import { useDocuments } from 'hooks/useDocuments';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { uploadColoringGridImage } from 'services';
import { getDocument } from 'services/api/firestore';
import { Status } from 'shared/enums';
import { FlexContainer } from 'shared/Typhography/flex-box';
import { strings } from 'strings';
import { getFileNameWithoutExtension } from 'utilities/file';

import { useGlobalModalContext } from '../../../../../../context/GlobalModal';
import { firestore, STORAGE_URL } from '../../../../../../services';
import { setColoringGridData } from '../../../../../../services/api/colloringGridApi';
import { isIndexedColorImage } from '../../../../../../services/utility/image/isIndexedColorImage';
import { Button, Input, Option, SelectForm } from '../../../../../../shared/Components';
import { SelectMultiForm } from '../../../../../../shared/Components/Select/SelectMultiForm';
import { ErrorMessage, Image, Text } from '../../../../../../shared/Typhography';
import { UploadField } from '../../../../../../validations/uploadImage';
import { getImageResolution } from '../../../ManageStoryScreen/utilities/getImageResolution';
import { UploadColoringGridImage } from '../UploadColoringGridImage';

import { ColoringGridField, coloringGridFormSchema } from './validations/ColoringGridFormSchema';

const ColoringGridStatusSelectOptions = Object.keys(ColoringGridStatusConstants).map((key) => ({
  value: key,
  label: (ColoringGridStatusConstants as any)[key]?.label,
}));

const defaultCreateFormValues: ComponentProps<typeof ColoringGridForm>['initialFormData'] = {
  publisher: { value: GOTIVA_ARTIST_DOC_ID, label: <>Gotiva</> },
  status: ColoringGridStatusConstants.approved,
  premiumTier: COLORING_GRID_PRICE_LIST[0],
  image: '',
  imageResolution: '',
  keywords: '',
  name: '',
  aiGenerated: false,
  forceSetPublishDate: false,
  collections: [],
};

interface ColoringGridFormProps {
  isPublished?: boolean;
  initialFormData?: ColoringGridField;
}
const ColoringGridForm = ({ initialFormData, isPublished }: ColoringGridFormProps) => {
  const [coloringGridImage, setColoringGridImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  /** Helps re-load select option data after form reset. */
  const [lastSuccessfulSubmitTime, setLastSuccessfulSubmitTime] = useState<number>(0);
  const history = useHistory();
  const { showModal } = useGlobalModalContext();

  const { control, formState, trigger, setValue, handleSubmit, watch, ...formMethods } = useForm({
    schema: coloringGridFormSchema,
    defaultValues: initialFormData ?? defaultCreateFormValues,
  });

  const { image } = watch();
  const onImageChange = useCallback(
    async (file: File) => {
      const url = file ? URL.createObjectURL(file) : '';

      if (file && !(await isIndexedColorImage(file))) {
        formMethods.setError('image', { message: 'Only indexed color mode allowed' });

        return;
      }

      if (image) {
        URL.revokeObjectURL(image);
      }

      setColoringGridImage(file);
      setValue('image', url);

      const resolution = await getImageResolution(url);
      setValue('imageResolution', `${resolution.width}x${resolution.height}`);
      setValue('name', getFileNameWithoutExtension(file));
    },
    [image],
  );

  const {
    docs: collectionsData,
    state: collectionsFetchState,
    error: collectionsFetchError,
  } = useDocuments(ALL_COLORING_GRID_CATEGORIES_QUERY);

  const {
    docs: artistsData,
    state: artistsFetchState,
    error: artistFetchError,
  } = useDocuments(ONLY_APPROVED_ARTISTS_QUERY);

  const mode = initialFormData ? 'edit' : 'create';
  const artistsDataLoading = artistsFetchState === 'loading';
  const isCollectionsDataLoading = collectionsFetchState === 'loading';

  const collectionOptions = useMemo(() => {
    return collectionsData
      ?.map((item) => ({
        value: item.id,
        label: item.data().name,
        id: item.id,
      }))
      .sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
        if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
        return 0;
      });
  }, [collectionsData, collectionsFetchState]);

  const artistOptions = useMemo(() => {
    const artistLookup: { [key: string]: Option } = {};

    artistsData?.forEach((item) => {
      const elem = {
        value: item.id,
        label: (
          <FlexContainer gap="1rem" alignItems="center" flexDirection="row">
            <Image
              size="profileImage"
              borderRadiusType="circle"
              src={item.data().profileImageUrl}
            />
            {item.data().Name} ({item.data().username})
          </FlexContainer>
        ),
        searchValue: item.data().Name,
      };

      artistLookup[item.id] = elem;
    });

    return Object.keys(artistLookup).map((key) => artistLookup[key]);
  }, [artistsData, artistsFetchState]);

  // Makes sure that dropdown options are loaded properly.
  // Especially important for artist options because of profile image.
  useEffect(() => {
    if (initialFormData) {
      const publisher =
        (artistOptions.find(
          (artist) => artist.value === initialFormData?.publisher?.value,
        ) as any) || initialFormData?.publisher;

      const premiumTier =
        COLORING_GRID_PRICE_LIST.find(
          (price) => price.value === initialFormData?.premiumTier?.value,
        ) || initialFormData?.premiumTier;

      setValue('publisher', publisher);
      setValue('premiumTier', premiumTier);
    }
  }, [initialFormData, artistOptions, lastSuccessfulSubmitTime]);

  const uploadFile = useCallback(async (id: string, file: File): Promise<string> => {
    const result = await uploadColoringGridImage(id, file);

    if (!result) {
      toast.error(strings.coloringGrid.create.toastError);

      throw new Error('Failed to upload coloring grid image');
    }

    return result.ref.fullPath;
  }, []);

  const submit = useCallback(() => {
    const proceed = handleSubmit(async (data) => {
      if (isSaving) {
        return;
      }

      setIsSaving(true);

      try {
        const artistId = data.publisher.value;
        const isSelectedArtistApproved =
          artistId === GOTIVA_ARTIST_DOC_ID
            ? true
            : (await getDocument(`usersWeb/${data.publisher.value}`)).data()?.status === 'approved';

        if (!isSelectedArtistApproved && data.status.value === Status.Approved) {
          toast.error('Cannot select non-approved artist for approved coloring grid');
          return;
        }

        const coloringGridsRef = collection(firestore, 'coloringGrids');
        const coloringGridId = data.id || doc(coloringGridsRef).id;

        const imagePath = coloringGridImage
          ? await uploadFile(coloringGridId, coloringGridImage)
          : data.image;

        const timestamp = serverTimestamp() as Timestamp;

        const isApprovedForTheFirstTime =
          (mode === 'create' && data.status.value === 'approved') ||
          (mode === 'edit' &&
            !isPublished &&
            data.status.value === 'approved' &&
            initialFormData?.status.value !== 'approved');

        const shouldSetPublishDate = data.forceSetPublishDate || isApprovedForTheFirstTime;

        const url = coloringGridImage
          ? `${STORAGE_URL}/${imagePath}?timestamp=${new Date().getTime()}`
          : imagePath;

        const resolution = await getImageResolution(url);

        const meta = {
          originalResolution: `${resolution.width}x${resolution.height}`,
        };

        const payload: any = {
          id: coloringGridId,
          name: data.name,
          _meta: meta,
          url: url,
          iconUrl: url,
          collections: (data.collections || []).map((collection) => collection.value),
          publisher: data.publisher.value,
          status: data.status.value,
          aiGenerated: data.aiGenerated,
          premiumTier: data.premiumTier.value,
          keywords: data.keywords.split(' '),
          updatedAt: Timestamp.now(),
          ...(!data.id
            ? {
                createdAt: Timestamp.now(),
              }
            : {}),
          ...(shouldSetPublishDate
            ? {
                publishedAt: timestamp,
              }
            : {}),
        };

        await setColoringGridData(payload as any);

        toast.success(
          data.id ? strings.coloringGrid.edit.toast : strings.coloringGrid.create.toast,
        );

        setColoringGridImage(null);

        mode === 'create' && formMethods.reset();
        setLastSuccessfulSubmitTime(new Date().getTime());
      } catch (error) {
        toast.error(
          data.id
            ? strings.coloringGrid.edit.toastErrorSave
            : strings.coloringGrid.create.toastErrorSave,
        );
      } finally {
        setIsSaving(false);
      }
    });

    proceed();
  }, [coloringGridImage, setIsSaving, isSaving, showModal, initialFormData]);

  const errors = formState.errors;

  const { message: imageErrorMessage } = (errors['image'] as any) || {};
  const isSubmitDisabled = artistsDataLoading || isCollectionsDataLoading || isSaving;
  const isFetchError = artistsFetchState === 'error' || collectionsFetchState === 'error';

  if (isFetchError) {
    return <>Error: {JSON.stringify(collectionsFetchError ?? artistFetchError ?? {})}</>;
  }

  return (
    <FormProvider
      control={control}
      formState={formState}
      setValue={setValue}
      trigger={trigger}
      handleSubmit={handleSubmit}
      watch={watch}
      {...formMethods}
    >
      <form>
        <FlexContainer
          flexDirection="row"
          flexWrap="wrap"
          gap="20px"
          justifyContent="center"
          alignContent="center"
        >
          <FlexContainer
            flexDirection="column"
            dimensions={{ width: '30%', minWidth: '300px' }}
            gap="10px"
            justifyContent="center"
            alignItems="center"
          >
            <UploadColoringGridImage
              disableDeleteOption={isPublished}
              onFileChange={onImageChange}
            />
            {imageErrorMessage && (
              <ErrorMessage style={{ position: 'relative', left: 0, bottom: 0 }}>
                {imageErrorMessage}
              </ErrorMessage>
            )}
          </FlexContainer>

          <FlexContainer
            dimensions={{ width: '100%' }}
            gap="50px"
            alignItems="center"
            flexDirection="column"
            margin={{ marginTop: '50px' }}
          >
            <FlexContainer
              dimensions={{ width: '100%' }}
              flexDirection="row"
              gap="50px"
              flexWrap="wrap"
            >
              <FlexContainer
                dimensions={{ flex: 1, minWidth: '300px', width: '100%' }}
                flexDirection="column"
                gap="50px"
                alignItems="center"
              >
                <Input
                  label="Name"
                  required
                  control={control}
                  type="text"
                  width="100%"
                  outerWidth="100%"
                  name="name"
                  error={errors['name']}
                  minWidth="200px"
                  placeholder="Enter Name"
                />
                <SelectForm
                  required
                  width="100%"
                  placeholder={strings.selectArtist}
                  values={artistOptions}
                  control={control}
                  name="publisher"
                  error={errors['publisher']}
                  label="Artist"
                  id="artist-select-dropdown"
                  includeCustomSearch
                />
                <SelectForm
                  required
                  width="100%"
                  placeholder={strings.selectPuzzleTier}
                  values={COLORING_GRID_PRICE_LIST}
                  control={control}
                  name="premiumTier"
                  label="Price"
                  id="tier-select-dropdown"
                  margin="33px 0 0 5px"
                />
                <SelectForm
                  required
                  id="status"
                  label="Status"
                  name="status"
                  placeholder="Select status"
                  control={control}
                  width="100%"
                  values={ColoringGridStatusSelectOptions}
                  error={errors['status']}
                />
              </FlexContainer>
              <FlexContainer
                dimensions={{ flex: 1, minWidth: '300px', width: '100%' }}
                flexDirection="column"
                gap="50px"
                alignItems="center"
              >
                <FlexContainer
                  gap="10px"
                  alignItems="center"
                  justifyContent="center"
                  dimensions={{ width: '50%', height: '100%' }}
                  flexDirection="column"
                >
                  <Input
                    control={control}
                    type="checkbox"
                    width="25px"
                    defaultChecked={false}
                    defaultValue={false}
                    name="aiGenerated"
                    placeholder={strings.aiGenerated}
                    outerWidth="50%"
                    maxOuterWidth="25px"
                  />
                  <Text size={0.8} textAlign="left">
                    AI Generated
                  </Text>
                </FlexContainer>
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-evenly"
                  alignItems="center"
                  gap="1rem"
                >
                  <Input
                    control={control}
                    type="checkbox"
                    width="25px"
                    minWidth="25px"
                    name="forceSetPublishDate"
                    defaultValue={false}
                  />
                  <Text size={0.8}>Force set publish date</Text>
                </FlexContainer>
                <SelectMultiForm
                  width="100%"
                  style={{ height: '60px' }}
                  required
                  placeholder={strings.selectCollection}
                  values={collectionOptions}
                  control={control}
                  name="collections"
                  error={errors['collections']}
                  label="Categories"
                  id="primary-collection-select-dropdown"
                />
                <div style={{ width: '400px', margin: '0 auto 40px' }} />
              </FlexContainer>
            </FlexContainer>
            <Input
              label="Keywords"
              required
              control={control}
              type="textarea"
              outerWidth="100%"
              height="120px"
              placeholder={UploadField.Keywords}
              name={UploadField.Keywords}
              error={errors[UploadField.Keywords]}
            />
          </FlexContainer>
        </FlexContainer>
      </form>

      <FlexContainer flexWrap="wrap" justifyContent="space-between">
        <Button
          style={{ minWidth: 200 }}
          variant="danger"
          disabled={isSubmitDisabled}
          height={40}
          onClick={() => history.push('/coloring-grids')}
        >
          {strings.coloringGrid.form.buttons.cancel}
        </Button>

        <Button
          style={{ minWidth: 200 }}
          variant="primary"
          disabled={isSubmitDisabled}
          height={40}
          onClick={() => submit()}
        >
          {strings.coloringGrid.form.buttons.save}
        </Button>
      </FlexContainer>
    </FormProvider>
  );
};

export default ColoringGridForm;
